/**
 * River v2 event names for the `checkout-ui` domain.
 *
 * Naming rule (River): the proto message carries the domain prefix
 * (`CheckoutUICheckoutRendered`) but the `eventName` we send DROPS it
 * (`CheckoutRendered`). River snake_cases the name to resolve the `oneof data`
 * arm, and the payload must nest under that arm key.
 *
 * Topic:  checkout-ui.tracking-events
 * Entity: checkout_ui_session  (entity id = checkout_id)
 */
export const RIVER_EVENT = {
  // Entry
  checkoutRendered: "CheckoutRendered",
  // Auth
  authRedirectTriggered: "AuthRedirectTriggered",
  authReturned: "AuthReturned",
  // Verification gates
  kycRedirectTriggered: "KycRedirectTriggered",
  questionnaireRedirectTriggered: "QuestionnaireRedirectTriggered",
  // IVR
  ivrScreenShown: "IvrScreenShown",
  ivrCompleted: "IvrCompleted",
  // Plan selection
  planSelectionShown: "PlanSelectionShown",
  paymentPlanSelected: "PaymentPlanSelected",
  planDetailsViewed: "PlanDetailsViewed",
  checkoutContinued: "CheckoutContinued",
  // Documents
  documentSignClicked: "DocumentSignClicked",
  documentDetailsOpened: "DocumentDetailsOpened",
  // Payment
  paymentMethodsShown: "PaymentMethodsShown",
  paymentMethodSelected: "PaymentMethodSelected",
  paymentSubmitted: "PaymentSubmitted",
  paymentResultShown: "PaymentResultShown",
  // Navigation / errors
  backButtonUsed: "BackButtonUsed",
  errorShown: "ErrorShown",
} as const;

export type RiverEventName = (typeof RIVER_EVENT)[keyof typeof RIVER_EVENT];

/**
 * `oneof data` arm keys — snake_case of the event name. The payload must nest
 * under these exactly, or River rejects the publish with HTTP 400.
 */
export const RIVER_ARM = {
  [RIVER_EVENT.checkoutRendered]: "checkout_rendered",
  [RIVER_EVENT.authRedirectTriggered]: "auth_redirect_triggered",
  [RIVER_EVENT.authReturned]: "auth_returned",
  [RIVER_EVENT.kycRedirectTriggered]: "kyc_redirect_triggered",
  [RIVER_EVENT.questionnaireRedirectTriggered]:
    "questionnaire_redirect_triggered",
  [RIVER_EVENT.ivrScreenShown]: "ivr_screen_shown",
  [RIVER_EVENT.ivrCompleted]: "ivr_completed",
  [RIVER_EVENT.planSelectionShown]: "plan_selection_shown",
  [RIVER_EVENT.paymentPlanSelected]: "payment_plan_selected",
  [RIVER_EVENT.planDetailsViewed]: "plan_details_viewed",
  [RIVER_EVENT.checkoutContinued]: "checkout_continued",
  [RIVER_EVENT.documentSignClicked]: "document_sign_clicked",
  [RIVER_EVENT.documentDetailsOpened]: "document_details_opened",
  [RIVER_EVENT.paymentMethodsShown]: "payment_methods_shown",
  [RIVER_EVENT.paymentMethodSelected]: "payment_method_selected",
  [RIVER_EVENT.paymentSubmitted]: "payment_submitted",
  [RIVER_EVENT.paymentResultShown]: "payment_result_shown",
  [RIVER_EVENT.backButtonUsed]: "back_button_used",
  [RIVER_EVENT.errorShown]: "error_shown",
} as const satisfies Record<RiverEventName, string>;

/** Proto enum string values. BigQuery stores the name, not the ordinal. */
export const RIVER_SELECTION_SOURCE = {
  backendDefault: "SELECTION_SOURCE_BACKEND_DEFAULT",
  uiFallback: "SELECTION_SOURCE_UI_FALLBACK",
  userSwitched: "SELECTION_SOURCE_USER_SWITCHED",
} as const;

export const RIVER_PAYMENT_TYPE = {
  payNow: "PAYMENT_TYPE_PAY_NOW",
  payNextMonth: "PAYMENT_TYPE_PAY_NEXT_MONTH",
  instalments: "PAYMENT_TYPE_INSTALMENTS",
} as const;

export const RIVER_METHOD_TYPE = {
  card: "METHOD_TYPE_CARD",
  applePay: "METHOD_TYPE_APPLE_PAY",
  wallet: "METHOD_TYPE_WALLET",
  newCard: "METHOD_TYPE_NEW_CARD",
} as const;

/** PaymentResultShown.Outcome. */
export const RIVER_OUTCOME = {
  succeeded: "OUTCOME_SUCCEEDED",
  failed: "OUTCOME_FAILED",
  cancelled: "OUTCOME_CANCELLED",
  declined: "OUTCOME_DECLINED",
} as const;

/**
 * IvrCompleted.Outcome — a DIFFERENT enum to RIVER_OUTCOME above, despite the
 * shared prefix. Sending a PaymentResultShown value here is a 400 the browser
 * never sees.
 */
export const RIVER_IVR_OUTCOME = {
  accepted: "OUTCOME_ACCEPTED",
  failed: "OUTCOME_FAILED",
  abandoned: "OUTCOME_ABANDONED",
} as const;

/**
 * PaymentSubmitted.MethodType stops at WALLET — unlike PaymentMethodsShown and
 * PaymentMethodSelected it has no NEW_CARD member, so a new-card submission
 * reports CARD. Separate constant so the type system enforces that.
 */
export const RIVER_SUBMIT_METHOD_TYPE = {
  card: "METHOD_TYPE_CARD",
  applePay: "METHOD_TYPE_APPLE_PAY",
  wallet: "METHOD_TYPE_WALLET",
} as const;

/**
 * Tenure is the discriminator the backend uses for plan shape: 0 is pay in
 * full, 1 is pay next month, anything higher is a split plan. Lives here rather
 * than in the plan-selection view so every call site maps it the same way.
 */
export function riverPaymentTypeFor(
  tenure: number,
): (typeof RIVER_PAYMENT_TYPE)[keyof typeof RIVER_PAYMENT_TYPE] {
  if (tenure === 0) return RIVER_PAYMENT_TYPE.payNow;
  if (tenure === 1) return RIVER_PAYMENT_TYPE.payNextMonth;
  return RIVER_PAYMENT_TYPE.instalments;
}
